import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { api } from "../lib/api";
import { STRATEGIC_BRIEFING_CHAPTERS, getBriefingValue } from "../lib/briefingResolver";
import { WhatsAppIcon } from "./FloatingWhatsApp";

interface BriefingDrawerProps {
  clientId: string | null;
  clientName: string;
  clientWhatsapp?: string | null;
  clientBusiness?: string | null;
  onClose: () => void;
}

interface BriefingData {
  responses: Record<string, unknown>;
  status: "draft" | "submitted";
  submittedAt: string | null;
}

interface MapSummary {
  id: string;
  title: string;
  shared: boolean;
  updatedAt: string;
}

const SECTIONS = STRATEGIC_BRIEFING_CHAPTERS;

export function BriefingDrawer({
  clientId,
  clientName,
  clientWhatsapp,
  clientBusiness,
  onClose,
}: BriefingDrawerProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<BriefingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [openingMap, setOpeningMap] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setData(null);
      return;
    }
    setLoading(true);
    api
      .get<BriefingData>(`/clients/${clientId}/briefing`)
      .then((res) => {
        setData(res);
      })
      .catch(() => {
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [clientId]);

  // Tecla ESC para sair do modal
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  async function abrirMapaMental() {
    if (!clientId) return;
    setOpeningMap(true);
    try {
      const maps = await api.get<MapSummary[]>(`/clients/${clientId}/maps`).catch(() => []);
      if (maps && maps.length > 0 && maps[0]?.id) {
        navigate(`/clientes/${clientId}/mapas/${maps[0].id}`);
      } else {
        const newMap = await api.post<{ id: string }>(`/clients/${clientId}/maps`, {
          title: `Planejamento Estratégico - ${clientName}`,
        });
        navigate(`/clientes/${clientId}/mapas/${newMap.id}`);
      }
      onClose();
    } catch {
      navigate(`/clientes/${clientId}/mapas`);
      onClose();
    } finally {
      setOpeningMap(false);
    }
  }

  function copySummary() {
    if (!data) return;
    const lines: string[] = [];
    lines.push(`📋 *DIAGNÓSTICO ESTRATÉGICO UNIK*`);
    lines.push(`👤 *Cliente:* ${clientName}`);
    if (clientBusiness) lines.push(`🏢 *Empresa:* ${clientBusiness}`);
    if (clientWhatsapp) lines.push(`📱 *WhatsApp:* ${clientWhatsapp}`);
    lines.push("");

    SECTIONS.forEach((sec) => {
      lines.push(`*${sec.title.toUpperCase()}*`);
      sec.fields.forEach((f) => {
        const val = getBriefingValue(
          data.responses,
          f.key,
          f.key === "name" ? clientName :
          f.key === "whatsapp" ? clientWhatsapp :
          f.key === "businessName" ? clientBusiness :
          undefined
        );
        if (val) {
          lines.push(`• *${f.label}:* ${String(val)}`);
        }
      });
      lines.push("");
    });

    navigator.clipboard.writeText(lines.join("\n").trim());
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  const rawPhone = clientWhatsapp ? clientWhatsapp.replace(/\D/g, "") : "";
  const waUrl = rawPhone
    ? `https://wa.me/${rawPhone.startsWith("55") ? rawPhone : `55${rawPhone}`}`
    : null;

  return (
    <div
      className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center p-3 sm:p-6 bg-black/85 backdrop-blur-md animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative flex h-full max-h-[92vh] w-full max-w-4xl flex-col rounded-[var(--radius-card)] border border-border-default/80 bg-[#160a0a]/98 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabeçalho Fixo do Modal */}
        <header className="flex flex-wrap items-start justify-between gap-4 border-b border-border-subtle/80 bg-[#200f0e]/95 p-5 sm:p-6 backdrop-blur-md">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="type-overline text-gold">Diagnóstico Estratégico</span>
              <span className="rounded-full bg-success/20 border border-success/30 px-2.5 py-0.5 text-[11px] font-medium text-success">
                22 Perguntas
              </span>
            </div>
            <h2 className="type-h2 mt-1 truncate text-text-cream">{clientName}</h2>
            {clientBusiness && (
              <p className="type-caption truncate text-text-secondary">{clientBusiness}</p>
            )}

            {/* Barra de Ações Rápidas */}
            <div className="mt-3 flex flex-wrap items-center gap-2.5 text-xs">
              <button
                type="button"
                onClick={abrirMapaMental}
                disabled={openingMap}
                className="inline-flex items-center gap-2 rounded-[var(--radius-control)] border border-gold/60 bg-gold/15 px-3.5 py-1.5 font-medium text-gold hover:bg-gold/25 hover:border-gold transition-all shadow-sm"
              >
                <span>🗺️</span>
                {openingMap ? "Abrindo Mapa..." : "Abrir Mapa Mental"}
              </button>

              {waUrl && (
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-[var(--radius-control)] border border-emerald-500/30 bg-emerald-950/40 px-3 py-1.5 font-medium text-emerald-400 hover:bg-emerald-900/50 transition-colors"
                >
                  <WhatsAppIcon className="h-4 w-4 text-emerald-400 shrink-0" />
                  WhatsApp
                </a>
              )}

              <button
                type="button"
                onClick={copySummary}
                className="inline-flex items-center gap-1.5 rounded-[var(--radius-control)] border border-border-default bg-surface-elevated px-3 py-1.5 font-medium text-text-secondary hover:text-text-primary transition-colors"
              >
                {copied ? "✓ Copiado!" : "Copiar Resumo"}
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-text-tertiary hover:bg-surface-elevated hover:text-text-primary transition-colors"
            aria-label="Fechar modal"
          >
            ✕
          </button>
        </header>

        {/* Conteúdo scrollável com fundo opaco e blur suave */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
          {loading && (
            <div className="flex flex-col gap-4">
              <div className="h-24 animate-pulse rounded-[var(--radius-card)] bg-[#241110]/95" />
              <div className="h-36 animate-pulse rounded-[var(--radius-card)] bg-[#241110]/95" />
              <div className="h-36 animate-pulse rounded-[var(--radius-card)] bg-[#241110]/95" />
            </div>
          )}

          {!loading && !data && (
            <div className="rounded-[var(--radius-card)] border border-border-subtle bg-[#1c0d0d]/95 p-12 text-center">
              <p className="type-body text-text-secondary">
                Nenhum diagnóstico submetido ainda para este cliente.
              </p>
              <button
                type="button"
                onClick={abrirMapaMental}
                className="mt-4 inline-flex items-center gap-2 rounded-[var(--radius-control)] border border-gold/40 bg-gold/10 px-4 py-2 text-xs font-medium text-gold hover:bg-gold/20 transition-colors"
              >
                <span>🗺️</span> Criar Primeiro Mapa Mental
              </button>
            </div>
          )}

          {!loading && data && (
            <>
              {SECTIONS.map((sec) => (
                <div
                  key={sec.title}
                  className="rounded-[var(--radius-card)] border border-border-default/70 bg-[#1c0d0d]/95 p-5 shadow-lg backdrop-blur-sm"
                >
                  <div className="flex items-center gap-2.5 border-b border-border-subtle/80 pb-3">
                    <span
                      className="h-3 w-3 rounded-full shadow-sm"
                      style={{ backgroundColor: sec.color }}
                    />
                    <h3 className="type-h3 text-text-cream">{sec.title}</h3>
                  </div>

                  <div className="mt-4 space-y-4">
                    {sec.fields.map((f) => {
                      const val = getBriefingValue(
                        data.responses,
                        f.key,
                        f.key === "name" ? clientName :
                        f.key === "whatsapp" ? clientWhatsapp :
                        f.key === "businessName" ? clientBusiness :
                        undefined
                      );
                      return (
                        <div key={f.key} className="space-y-1">
                          <label className="type-label block text-xs font-semibold uppercase tracking-wider text-text-tertiary">
                            {f.label}
                          </label>
                          <div className="rounded-[var(--radius-control-sm)] bg-[#140808]/90 p-3 text-sm leading-relaxed text-text-primary border border-border-subtle/60 shadow-inner whitespace-pre-wrap">
                            {val ? (
                              String(val)
                            ) : (
                              <span className="italic text-text-tertiary">Não preenchido</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
